import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Journal — one private, reflective entry store shared by the Mental and
 * Spiritual systems (two streams over one entity, not two duplicate modules).
 *
 * Mental: journal, reflection, mental models, deep-work notes (strictly local).
 * Spiritual: gratitude, reflection, prayer/meditation notes.
 *
 * Guardrails: no moral scoring, no guilt board, no pressure labels. Entries are
 * for the owner's own thinking; the system only counts gently and never ranks.
 */

export type JournalStream = 'mental' | 'spiritual';
export const JOURNAL_STREAMS: readonly JournalStream[] = ['mental', 'spiritual'] as const;

export type JournalKind = 'journal' | 'reflection' | 'gratitude' | 'model' | 'practice';
export const JOURNAL_KINDS: readonly JournalKind[] = ['journal', 'reflection', 'gratitude', 'model', 'practice'] as const;

/** Suggested entry kinds per stream (UI hint only; the model accepts any kind). */
export const STREAM_KINDS: Record<JournalStream, readonly JournalKind[]> = {
  mental: ['journal', 'reflection', 'model', 'practice'],
  spiritual: ['gratitude', 'reflection', 'practice'],
};

export interface JournalEntry extends RecordEnvelope {
  readonly kind: 'journal_entry';
  readonly stream: JournalStream;
  readonly entryKind: JournalKind;
  readonly title: string;
  readonly body: string;
  /** ISO date the entry is counted under. */
  readonly occurredOn: string;
}

export interface JournalEntryInput {
  readonly stream: JournalStream;
  readonly entryKind?: JournalKind;
  readonly title?: string;
  readonly body: string;
  readonly occurredOn?: string;
  readonly privacyClass?: PrivacyClass;
}

/** Default privacy by stream: mental thinking is strictly local. */
export function defaultJournalPrivacy(stream: JournalStream): PrivacyClass {
  return stream === 'mental' ? 'strict_local' : 'sensitive_private';
}

export interface JournalSummary {
  readonly count: number;
  readonly daysJournaled: number;
  readonly byKind: Record<JournalKind, number>;
}

const dayOf = (iso: string): string => iso.slice(0, 10);

/** Gentle counts for one stream over [fromIso, toIso] (inclusive). Pure. */
export function summarizeJournal(
  entries: readonly Pick<JournalEntry, 'stream' | 'entryKind' | 'occurredOn'>[],
  stream: JournalStream,
  fromIso: string,
  toIso: string,
): JournalSummary {
  const from = dayOf(fromIso);
  const to = dayOf(toIso);
  const byKind: Record<JournalKind, number> = { journal: 0, reflection: 0, gratitude: 0, model: 0, practice: 0 };
  const days = new Set<string>();
  let count = 0;
  for (const e of entries) {
    if (e.stream !== stream) continue;
    const d = dayOf(e.occurredOn);
    if (d < from || d > to) continue;
    count += 1;
    byKind[e.entryKind] += 1;
    days.add(d);
  }
  return { count, daysJournaled: days.size, byKind };
}
